// Página ProductForm - Formulario completo para crear/editar productos
// Incluye: Cloudinary, editor de texto enriquecido, dimensiones, color, variantes

import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useProduct } from '../../hooks/useFirestore'
import { productsService } from '../../services/firestore.service'
import { uploadImage } from '../../services/cloudinary.service'
import { useCategories } from '../../hooks/useCategories'
import { useSubcategories } from '../../hooks/useCategories'
import { useBusinessRulesContext } from '../../hooks/useBusinessRulesContext'
import { getAllSuppliers } from '../../services/suppliers.service'
import { getCategory } from '../../services/categories.service'
import { formatCurrencyInput, parseCurrencyInput, formatCurrency } from '../../utils/helpers'
import { generateSerialNumber, generateSkuForProduct } from '../../services/productIdentifiers.service'
import Button from '../../components/Button'
import RichTextEditor from '../../components/RichTextEditor'
import ColorPicker from '../../components/ColorPicker'
import MultiSelect from '../../components/MultiSelect'
import Toast from '../../components/Toast'
import SupplierCards from '../../components/admin/SupplierCards'
import VariantStockModal from '../../components/admin/VariantStockModal'
import './ProductForm.css'

/**
 * Página ProductForm
 * Formulario completo para crear o editar un producto
 */
function ProductForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id
  const { rules } = useBusinessRulesContext()

  // Obtener producto si estamos editando
  const { product, loading: loadingProduct } = useProduct(id)

  // Obtener categorías dinámicas
  const { categories } = useCategories()

  // Estado para proveedores
  const [suppliers, setSuppliers] = useState([])

  // Estado del formulario
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    shortDescription: '',
    price: '',
    specialPrice: '', // Precio especial/oferta (opcional)
    categoryId: '', // ID de la categoría
    subcategoryId: '', // ID de la subcategoría (opcional)
    supplierIds: [], // IDs de los proveedores (array, opcional)
    active: true,
    featured: false, // Producto destacado para la página principal
    color: '#2c3e50',
    material: '',
    brand: '',
    // Dimensiones
    dimensions: {
      width: '',
      height: '',
      depth: '',
      weight: ''
    },
    // Variantes
    variants: [],
    images: [],
    // Costos asociados
    inheritedCosts: [], // Array de { costId, excluded: boolean }
    specificCosts: [] // Array de { id, name, amount, description }
  })

  // Obtener subcategorías dinámicas (después de formData para poder usar formData.categoryId)
  const { subcategories } = useSubcategories(formData.categoryId)

  // Cargar proveedores al inicio
  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const suppliersData = await getAllSuppliers()
        setSuppliers(suppliersData)
      } catch (error) {
        console.error('Error cargando proveedores:', error)
      }
    }
    loadSuppliers()
  }, [])

  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  
  const [existingImages, setExistingImages] = useState([])

  // Estados para variantes
  const [variantType, setVariantType] = useState('') // 'size', 'color', 'material'
  const [variantName, setVariantName] = useState('')
  const [variantPrice, setVariantPrice] = useState('')
  const [variantSku, setVariantSku] = useState('')
  const [variantSerialNumber, setVariantSerialNumber] = useState('')
  const [variantImages, setVariantImages] = useState([])
  const [variantImagePreviews, setVariantImagePreviews] = useState([])
  const [variantVideos, setVariantVideos] = useState([])
  const [variantVideoInput, setVariantVideoInput] = useState('')
  const [uploadingVariantImages, setUploadingVariantImages] = useState(false)
  const [variantUploadProgress, setVariantUploadProgress] = useState({})
  const [activeTab, setActiveTab] = useState('general')
  const [editingVariantIndex, setEditingVariantIndex] = useState(null)
  const [editVariantType, setEditVariantType] = useState('')
  const [editVariantName, setEditVariantName] = useState('')
  const [editVariantPrice, setEditVariantPrice] = useState('')
  const [editVariantSku, setEditVariantSku] = useState('')
  const [editVariantSerialNumber, setEditVariantSerialNumber] = useState('')
  const [editVariantImages, setEditVariantImages] = useState([])
  const [editVariantVideos, setEditVariantVideos] = useState([])
  const [editVariantVideoInput, setEditVariantVideoInput] = useState('')
  const [uploadingEditVariantImages, setUploadingEditVariantImages] = useState(false)
  
  // Estados para costos específicos
  const [newSpecificCost, setNewSpecificCost] = useState({ name: '', amount: '', description: '' })
  const [editingSpecificCostIndex, setEditingSpecificCostIndex] = useState(null)
  
  // Estado para modal de stock de variante
  const [stockModalData, setStockModalData] = useState(null)
  
  // Calcular precio final (precio base + costos)
  const calculateFinalPrice = () => {
    const basePrice = parseFloat(formData.price) || 0
    
    // Sumar costos heredados no excluidos
    const inheritedCostsTotal = categoryCosts.reduce((sum, cost) => {
      const inherited = formData.inheritedCosts.find(ic => ic.costId === (cost.id || cost.name))
      if (inherited && !inherited.excluded) {
        return sum + (cost.amount || 0)
      }
      return sum
    }, 0)
    
    // Sumar costos específicos
    const specificCostsTotal = formData.specificCosts.reduce((sum, cost) => {
      return sum + (cost.amount || 0)
    }, 0)
    
    return basePrice + inheritedCostsTotal + specificCostsTotal
  }
  
  // Toggle exclusión de costo heredado
  const toggleInheritedCost = (costId) => {
    setFormData(prev => {
      const existing = prev.inheritedCosts.find(ic => ic.costId === costId)
      if (existing) {
        return {
          ...prev,
          inheritedCosts: prev.inheritedCosts.map(ic =>
            ic.costId === costId ? { ...ic, excluded: !ic.excluded } : ic
          )
        }
      } else {
        return {
          ...prev,
          inheritedCosts: [...prev.inheritedCosts, { costId, excluded: false }]
        }
      }
    })
  }
  
  // Agregar costo específico
  const handleAddSpecificCost = () => {
    if (!newSpecificCost.name.trim() || !newSpecificCost.amount) {
      setNotification({
        type: 'error',
        message: 'El nombre y el monto son requeridos'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    
    const cost = {
      id: Date.now().toString(),
      name: newSpecificCost.name.trim(),
      amount: parseCurrencyInput(newSpecificCost.amount),
      description: newSpecificCost.description.trim() || ''
    }
    
    setFormData(prev => ({
      ...prev,
      specificCosts: [...prev.specificCosts, cost]
    }))
    
    setNewSpecificCost({ name: '', amount: '', description: '' })
  }
  
  // Editar costo específico
  const handleEditSpecificCost = (index) => {
    const cost = formData.specificCosts[index]
    setNewSpecificCost({
      name: cost.name,
      amount: formatCurrencyInput(cost.amount),
      description: cost.description || ''
    })
    setEditingSpecificCostIndex(index)
  }
  
  // Guardar edición de costo específico
  const handleSaveSpecificCost = () => {
    if (!newSpecificCost.name.trim() || !newSpecificCost.amount) {
      setNotification({
        type: 'error',
        message: 'El nombre y el monto son requeridos'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    
    const updatedCost = {
      ...formData.specificCosts[editingSpecificCostIndex],
      name: newSpecificCost.name.trim(),
      amount: parseCurrencyInput(newSpecificCost.amount),
      description: newSpecificCost.description.trim() || ''
    }
    
    setFormData(prev => ({
      ...prev,
      specificCosts: prev.specificCosts.map((cost, idx) =>
        idx === editingSpecificCostIndex ? updatedCost : cost
      )
    }))
    
    setNewSpecificCost({ name: '', amount: '', description: '' })
    setEditingSpecificCostIndex(null)
  }
  
  // Eliminar costo específico
  const handleDeleteSpecificCost = (index) => {
    setFormData(prev => ({
      ...prev,
      specificCosts: prev.specificCosts.filter((_, idx) => idx !== index)
    }))
  }
  
  // Cancelar edición de costo específico
  const handleCancelSpecificCostEdit = () => {
    setNewSpecificCost({ name: '', amount: '', description: '' })
    setEditingSpecificCostIndex(null)
  }

  // Cargar datos del producto si estamos editando
  useEffect(() => {
    if (isEditing && product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        shortDescription: product.shortDescription || '',
        price: product.price?.toString() || '',
        specialPrice: product.specialPrice?.toString() || '',
        categoryId: product.categoryId || product.category || '', // Compatibilidad con datos antiguos
        subcategoryId: product.subcategoryId || '',
        // Compatibilidad: si tiene supplierId (antiguo), convertirlo a array
        supplierIds: product.supplierIds || (product.supplierId ? [product.supplierId] : []),
        active: product.active !== false,
        featured: product.featured === true, // Producto destacado
        color: product.color || '#2c3e50',
        material: product.material || '',
        brand: product.brand || '',
        dimensions: product.dimensions || { width: '', height: '', depth: '', weight: '' },
        variants: product.variants || [],
        images: product.images || [],
        inheritedCosts: product.inheritedCosts || [],
        specificCosts: product.specificCosts || []
      })
      setExistingImages(product.images || [])
    }
  }, [product, isEditing])

  // Estado para costos de la categoría
  const [categoryCosts, setCategoryCosts] = useState([])
  
  // Cargar costos de la categoría cuando cambia
  useEffect(() => {
    const loadCategoryCosts = async () => {
      if (formData.categoryId) {
        try {
          const category = await getCategory(formData.categoryId)
          setCategoryCosts(category?.preparationCosts || [])
          
          // Si es un producto nuevo o no tiene inheritedCosts, inicializar con todos los costos no excluidos
          if (category?.preparationCosts && category.preparationCosts.length > 0) {
            setFormData(prev => {
              // Si ya tiene inheritedCosts, mantenerlos pero agregar los nuevos costos que no existan
              const existingCostIds = (prev.inheritedCosts || []).map(ic => ic.costId)
              const newCosts = category.preparationCosts
                .filter(cost => !existingCostIds.includes(cost.id || cost.name))
                .map(cost => ({
                  costId: cost.id || cost.name,
                  excluded: false
                }))
              
              return {
                ...prev,
                inheritedCosts: [
                  ...(prev.inheritedCosts || []),
                  ...newCosts
                ]
              }
            })
          }
        } catch (error) {
          console.error('Error cargando costos de categoría:', error)
          setCategoryCosts([])
        }
      } else {
        setCategoryCosts([])
      }
    }
    loadCategoryCosts()
  }, [formData.categoryId])
  
  // Actualizar subcategorías cuando cambia la categoría
  // El hook useSubcategories ya se actualiza automáticamente cuando cambia categoryId
  // No necesitamos llamar refreshSubcategories manualmente

  // Manejar cambios en los campos
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }
      // Si cambia la categoría, limpiar la subcategoría y resetear costos heredados
      if (name === 'categoryId') {
        newData.subcategoryId = ''
        newData.inheritedCosts = []
      }
      return newData
    })
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  // Estado para rastrear qué campos están siendo editados (sin formatear)
  const [editingFields, setEditingFields] = useState(new Set())

  // Manejar cambios en campos de precio (permitir escribir coma como decimal)
  const handlePriceChange = (e) => {
    const { name, value } = e.target
    
    // Permitir escribir números, punto, coma y $
    // Remover $ si el usuario lo escribe manualmente
    let cleanedValue = value.replace(/[^0-9.,]/g, '')
    
    // Si está vacío, guardar vacío
    if (!cleanedValue) {
      setFormData(prev => ({
        ...prev,
        [name]: ''
      }))
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }))
      }
      return
    }
    
    // Convertir punto a coma para decimales (formato argentino)
    // Si el usuario escribe punto, convertirlo a coma
    cleanedValue = cleanedValue.replace('.', ',')
    
    // Validar que solo tenga una coma decimal
    const parts = cleanedValue.split(',')
    if (parts.length > 2) {
      // Múltiples comas, mantener solo la primera
      cleanedValue = parts[0] + ',' + parts.slice(1).join('')
    }
    
    // Guardar el valor con coma (se convertirá a punto internamente en onBlur)
    setFormData(prev => ({
      ...prev,
      [name]: cleanedValue
    }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  // Manejar cuando el usuario hace foco en un campo de precio
  const handlePriceFocus = (e) => {
    const { name } = e.target
    setEditingFields(prev => new Set(prev).add(name))
  }

  // Manejar cuando el usuario termina de escribir (onBlur)
  const handlePriceBlur = (e) => {
    const { name } = e.target
    const value = formData[name]
    
    // Parsear y normalizar el valor (parseCurrencyInput maneja coma como decimal)
    const numericValue = parseCurrencyInput(value || '0')
    
    // Guardar como string numérico normalizado (con punto para cálculos internos)
    setFormData(prev => ({
      ...prev,
      [name]: numericValue > 0 ? numericValue.toString() : ''
    }))
    
    // Remover del conjunto de campos en edición
    setEditingFields(prev => {
      const newSet = new Set(prev)
      newSet.delete(name)
      return newSet
    })
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  // Manejar cambios en dimensiones
  const handleDimensionChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        [field]: value
      }
    }))
  }

  // Manejar cambio de descripción enriquecida
  const handleDescriptionChange = (value) => {
    setFormData(prev => ({ ...prev, description: value }))
    if (errors.description) {
      setErrors(prev => ({ ...prev, description: '' }))
    }
  }

  // Manejar cambio de color
  const handleColorChange = (color) => {
    setFormData(prev => ({ ...prev, color }))
  }

  const handleVariantImageChange = async (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    setUploadingVariantImages(true)
    const tempPreviews = []
    const uploadedImages = []

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const reader = new FileReader()
        reader.onloadend = () => {
          tempPreviews.push(reader.result)
          setVariantImagePreviews(prev => [...prev, reader.result])
        }
        reader.readAsDataURL(file)
      }

      await new Promise(resolve => setTimeout(resolve, 100))

      for (let i = 0; i < files.length; i++) {
        const file = files[i]

        try {
          const uploadResult = await uploadImage(
            file,
            'products',
            (progress) => {
              setVariantUploadProgress(prev => ({
                ...prev,
                [file.name]: progress
              }))
            }
          )

          uploadedImages.push(uploadResult)

          setVariantImagePreviews(prev => {
            const updated = [...prev]
            const previewIndex = variantImagePreviews.length + i
            if (updated[previewIndex]) {
              updated[previewIndex] = uploadResult.url
            }
            return updated
          })
        } catch (error) {
          console.error(`Error subiendo imagen ${file.name}:`, error)
          setVariantImagePreviews(prev => prev.filter((_, idx) => idx !== variantImagePreviews.length + i))
          setNotification({
            type: 'error',
            message: `Error al subir ${file.name}: ${error.message}`
          })
          setTimeout(() => setNotification(null), 5000)
        }
      }

      setVariantImages(prev => [...prev, ...uploadedImages])
    } catch (error) {
      console.error('Error procesando imágenes:', error)
      setNotification({
        type: 'error',
        message: 'Error al procesar las imágenes'
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setUploadingVariantImages(false)
      setVariantUploadProgress({})
    }

    e.target.value = ''
  }

  const removeVariantImage = (index) => {
    setVariantImagePreviews(prev => prev.filter((_, i) => i !== index))
    setVariantImages(prev => prev.filter((_, i) => i !== index))
  }

  const handleAddVariantVideo = () => {
    const value = String(variantVideoInput || '').trim()
    if (!value) return
    setVariantVideos(prev => [...prev, value])
    setVariantVideoInput('')
  }

  const handleRemoveVariantVideo = (index) => {
    setVariantVideos(prev => prev.filter((_, i) => i !== index))
  }

  // Agregar variante
  const handleAddVariant = async () => {
    if (!variantType || !variantName) {
      setNotification({
        type: 'warning',
        message: 'Por favor completa el tipo y nombre de la variante'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (!variantSku.trim() && rules?.productIdentification?.skuAuto?.enabled !== true) {
      setNotification({
        type: 'warning',
        message: 'El SKU de la variante es requerido'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    let finalSku = variantSku.trim().toUpperCase()
    if (!finalSku && rules?.productIdentification?.skuAuto?.enabled === true) {
      try {
        const skuPayload = {
          name: formData.name,
          brand: formData.brand,
          collection: formData.collection,
          color: variantType === 'color' ? variantName : formData.color,
          colorName: variantType === 'color' ? variantName : formData.colorName,
          sizes: variantType === 'size' ? [variantName] : []
        }
        const { sku } = await generateSkuForProduct({ ...skuPayload, id }, rules)
        finalSku = sku.toUpperCase()
      } catch (e) {
        setNotification({
          type: 'error',
          message: e?.message || 'No se pudo generar SKU'
        })
        setTimeout(() => setNotification(null), 5000)
        return
      }
    }

    let finalSerialNumber = String(variantSerialNumber || '').trim()
    if (!finalSerialNumber && rules?.productIdentification?.serialAuto?.enabled === true) {
      try {
        const digits = rules?.productIdentification?.serialAuto?.digits ?? 15
        const serial = await generateSerialNumber(digits)
        finalSerialNumber = serial
      } catch (e) {
        setNotification({
          type: 'error',
          message: e?.message || 'No se pudo generar número de serie'
        })
        setTimeout(() => setNotification(null), 5000)
        return
      }
    }

    const newVariant = {
      type: variantType,
      name: variantName,
      price: variantPrice ? parseFloat(variantPrice) : null,
      sku: finalSku,
      serialNumber: finalSerialNumber,
      images: variantImages,
      videos: variantVideos
    }

    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, newVariant]
    }))

    // Limpiar campos
    setVariantType('')
    setVariantName('')
    setVariantPrice('')
    setVariantSku('')
    setVariantSerialNumber('')
    setVariantImages([])
    setVariantImagePreviews([])
    setVariantVideos([])
    setVariantVideoInput('')
  }

  // Eliminar variante
  const handleRemoveVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }))
  }

  // Abrir modal de stock para variante
  const openStockModal = (variant, variantIndex) => {
    if (!isEditing) return
    setStockModalData({
      variant,
      variantIndex,
      productId: id
    })
  }

  const beginEditVariant = (index) => {
    const variant = formData.variants[index]
    if (!variant) return
    setEditingVariantIndex(index)
    setEditVariantType(variant.type || '')
    setEditVariantName(variant.name || '')
    setEditVariantPrice(variant.price !== null && variant.price !== undefined ? String(variant.price) : '')
    setEditVariantSku(variant.sku || '')
    setEditVariantSerialNumber(variant.serialNumber || '')
    setEditVariantImages(Array.isArray(variant.images) ? variant.images : [])
    setEditVariantVideos(Array.isArray(variant.videos) ? variant.videos : [])
    setEditVariantVideoInput('')
  }

  const cancelEditVariant = () => {
    setEditingVariantIndex(null)
    setEditVariantType('')
    setEditVariantName('')
    setEditVariantPrice('')
    setEditVariantSku('')
    setEditVariantSerialNumber('')
    setEditVariantImages([])
    setEditVariantVideos([])
    setEditVariantVideoInput('')
  }

  const handleEditVariantImageChange = async (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    setUploadingEditVariantImages(true)
    const uploadedImages = []

    try {
      for (const file of files) {
        const uploadResult = await uploadImage(file, 'products')
        uploadedImages.push(uploadResult)
      }
      setEditVariantImages(prev => [...prev, ...uploadedImages])
    } catch (error) {
      setNotification({
        type: 'error',
        message: `Error al subir imágenes: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setUploadingEditVariantImages(false)
    }

    e.target.value = ''
  }

  const removeEditVariantImage = (index) => {
    setEditVariantImages(prev => prev.filter((_, i) => i !== index))
  }

  const handleAddEditVariantVideo = () => {
    const value = String(editVariantVideoInput || '').trim()
    if (!value) return
    setEditVariantVideos(prev => [...prev, value])
    setEditVariantVideoInput('')
  }

  const handleRemoveEditVariantVideo = (index) => {
    setEditVariantVideos(prev => prev.filter((_, i) => i !== index))
  }

  const handleSaveEditedVariant = async () => {
    if (editingVariantIndex === null) return

    if (!editVariantType || !editVariantName) {
      setNotification({
        type: 'warning',
        message: 'Por favor completa el tipo y nombre de la variante'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (!editVariantSku.trim() && rules?.productIdentification?.skuAuto?.enabled !== true) {
      setNotification({
        type: 'warning',
        message: 'El SKU de la variante es requerido'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    let finalSku = editVariantSku.trim().toUpperCase()
    if (!finalSku && rules?.productIdentification?.skuAuto?.enabled === true) {
      try {
        const skuPayload = {
          name: formData.name,
          brand: formData.brand,
          collection: formData.collection,
          color: editVariantType === 'color' ? editVariantName : formData.color,
          colorName: editVariantType === 'color' ? editVariantName : formData.colorName,
          sizes: editVariantType === 'size' ? [editVariantName] : []
        }
        const { sku } = await generateSkuForProduct({ ...skuPayload, id }, rules)
        finalSku = sku.toUpperCase()
      } catch (e) {
        setNotification({
          type: 'error',
          message: e?.message || 'No se pudo generar SKU'
        })
        setTimeout(() => setNotification(null), 5000)
        return
      }
    }

    let finalSerialNumber = String(editVariantSerialNumber || '').trim()
    if (!finalSerialNumber && rules?.productIdentification?.serialAuto?.enabled === true) {
      try {
        const digits = rules?.productIdentification?.serialAuto?.digits ?? 15
        const serial = await generateSerialNumber(digits)
        finalSerialNumber = serial
      } catch (e) {
        setNotification({
          type: 'error',
          message: e?.message || 'No se pudo generar número de serie'
        })
        setTimeout(() => setNotification(null), 5000)
        return
      }
    }

    const updatedVariant = {
      type: editVariantType,
      name: editVariantName,
      price: editVariantPrice ? parseCurrencyInput(editVariantPrice) : null,
      sku: finalSku,
      serialNumber: finalSerialNumber,
      images: editVariantImages,
      videos: editVariantVideos
    }

    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((variant, idx) =>
        idx === editingVariantIndex ? updatedVariant : variant
      )
    }))

    setNotification({
      type: 'success',
      message: 'Variante actualizada'
    })
    setTimeout(() => setNotification(null), 2500)
    cancelEditVariant()
  }

  // Validar formulario
  const validate = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido'
    }

    if (!formData.description.trim() || formData.description === '<p><br></p>') {
      newErrors.description = 'La descripción es requerida'
    }

    // Precio es opcional por ahora
    if (formData.price && parseFloat(formData.price) < 0) {
      newErrors.price = 'El precio no puede ser negativo'
    }

    // Validar precio especial
    if (formData.specialPrice && parseFloat(formData.specialPrice) < 0) {
      newErrors.specialPrice = 'El precio especial no puede ser negativo'
    }

    // Validar que precio especial sea menor que precio base (si ambos tienen valor)
    if (formData.price && formData.specialPrice) {
      const price = parseFloat(formData.price)
      const specialPrice = parseFloat(formData.specialPrice)
      if (specialPrice > 0 && specialPrice >= price) {
        newErrors.specialPrice = 'El precio especial debe ser menor que el precio base'
      }
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'La categoría es requerida'
    }

    // Stock se gestiona desde el módulo de stock, no desde aquí

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Guardar producto
  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    // Prevenir múltiples envíos
    if (saving) {
      return
    }

    if (!validate()) {
      return
    }

    setSaving(true)

    try {
      const allImages = [...existingImages]

      const productData = {
        name: formData.name.trim(),
        description: formData.description,
        shortDescription: formData.shortDescription.trim(),
        price: formData.price ? parseFloat(formData.price) : 0,
        specialPrice: formData.specialPrice ? parseFloat(formData.specialPrice) : 0,
        categoryId: formData.categoryId,
        subcategoryId: formData.subcategoryId || null, // Opcional
        supplierIds: formData.supplierIds && formData.supplierIds.length > 0 ? formData.supplierIds : [], // Array de proveedores (opcional)
        active: formData.active,
        featured: formData.featured === true, // Producto destacado
        color: formData.color,
        material: formData.material.trim(),
        brand: formData.brand.trim(),
        dimensions: {
          width: formData.dimensions.width ? parseFloat(formData.dimensions.width) : null,
          height: formData.dimensions.height ? parseFloat(formData.dimensions.height) : null,
          depth: formData.dimensions.depth ? parseFloat(formData.dimensions.depth) : null,
          weight: formData.dimensions.weight ? parseFloat(formData.dimensions.weight) : null
        },
        variants: formData.variants,
        images: allImages,
        inheritedCosts: formData.inheritedCosts || [],
        specificCosts: formData.specificCosts || []
      }

      // Los timestamps (createdAt, updatedAt) se agregan automáticamente en el servicio de Firestore

      console.log('Guardando producto:', { isEditing, productData })

      if (isEditing) {
        await productsService.update(id, productData)
        setNotification({
          type: 'success',
          message: 'Producto actualizado correctamente'
        })
      } else {
        const productId = await productsService.create(productData)
        console.log('Producto creado con ID:', productId)
        setNotification({
          type: 'success',
          message: 'Producto creado correctamente'
        })
      }

      // Navegar después de un pequeño delay para asegurar que el guardado se complete
      setTimeout(() => {
        navigate('/admin/productos')
      }, 1500)
    } catch (error) {
      console.error('Error al guardar producto:', error)
      setNotification({
        type: 'error',
        message: `Error al guardar el producto: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
      setSaving(false)
    }
  }

  if (isEditing && loadingProduct) {
    return (
      <div className="loading-state">
        <p>Cargando producto...</p>
      </div>
    )
  }

  return (
    <div className="product-form-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />
      <div className="form-header">
        <div className="header-left">
          <h2>{isEditing ? 'Editar Producto' : 'Nuevo Producto'}</h2>
          {isEditing && product?.priceHistory && product.priceHistory.length > 0 && (
            <Link 
              to={`/admin/productos/${id}/historial-precios`}
              className="price-history-link"
            >
              📊 Ver Historial de Precios ({product.priceHistory.length} cambios)
            </Link>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/admin/productos')}
        >
          ← Volver
        </Button>
      </div>

      <div className="product-tabs">
        {[
          { key: 'general', label: 'General' },
          { key: 'costos', label: 'Costos' },
          { key: 'variantes', label: 'Variantes' },
          { key: 'estado', label: 'Estado' }
        ].map(tab => (
          <button
            key={tab.key}
            type="button"
            className={`product-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="product-form">
        {activeTab === 'general' ? (
        <div className="form-section">
          <h3 className="section-title">Información del Producto</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">
                Nombre del Producto <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ej: Sofá Oslo Moderno"
                className={errors.name ? 'error' : ''}
                disabled={saving}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="categoryId">
                Categoría <span className="required">*</span>
              </label>
              <select
                id="categoryId"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                className={errors.categoryId ? 'error' : ''}
                disabled={saving}
              >
                <option value="">Seleccionar categoría</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors.categoryId && <span className="error-message">{errors.categoryId}</span>}
              {categories.length === 0 && (
                <small className="form-hint">
                  No hay categorías disponibles. <Link to="/admin/configuraciones/categorias">Crear categorías</Link>
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="subcategoryId">
                Subcategoría <small>(opcional)</small>
              </label>
              <select
                id="subcategoryId"
                name="subcategoryId"
                value={formData.subcategoryId}
                onChange={handleChange}
                disabled={saving || !formData.categoryId || subcategories.length === 0}
              >
                <option value="">Sin subcategoría</option>
                {subcategories.map(subcategory => (
                  <option key={subcategory.id} value={subcategory.id}>
                    {subcategory.name}
                  </option>
                ))}
              </select>
              {formData.categoryId && subcategories.length === 0 && (
                <small className="form-hint">
                  No hay subcategorías para esta categoría. <Link to="/admin/configuraciones/subcategorias">Crear subcategorías</Link>
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="supplierIds">
                Proveedores <small>(opcional, puede seleccionar múltiples)</small>
              </label>
              {suppliers.length === 0 ? (
                <div className="no-suppliers-message">
                  <p>No hay proveedores disponibles.</p>
                  <Link to="/admin/proveedores/nuevo">Crear proveedor</Link>
                </div>
              ) : (
                <SupplierCards
                  suppliers={suppliers}
                  selectedIds={formData.supplierIds}
                  onChange={(selectedIds) => {
                    setFormData(prev => ({
                      ...prev,
                      supplierIds: selectedIds
                    }))
                  }}
                  disabled={saving}
                />
              )}
            </div>

            <div className="form-group">
              <label htmlFor="brand">Marca</label>
              <input
                type="text"
                id="brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                placeholder="Ej: PinoHome"
                disabled={saving}
              />
            </div>
          </div>

          <h4 className="subsection-title">Precio</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="price">
                Precio Base <small>(opcional)</small>
              </label>
              <input
                type="text"
                id="price"
                name="price"
                value={
                  editingFields.has('price')
                    ? (formData.price ? `$${formData.price.replace('.', ',')}` : '')
                    : (formData.price ? formatCurrencyInput(formData.price, true) : '')
                }
                onChange={handlePriceChange}
                onFocus={handlePriceFocus}
                onBlur={handlePriceBlur}
                placeholder="$0"
                className={errors.price ? 'error' : ''}
                disabled={saving}
              />
              {errors.price && <span className="error-message">{errors.price}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="specialPrice">
                Precio Especial/Oferta <small>(opcional)</small>
              </label>
              <input
                type="text"
                id="specialPrice"
                name="specialPrice"
                value={
                  editingFields.has('specialPrice')
                    ? (formData.specialPrice ? `$${formData.specialPrice.replace('.', ',')}` : '')
                    : (formData.specialPrice ? formatCurrencyInput(formData.specialPrice, true) : '')
                }
                onChange={handlePriceChange}
                onFocus={handlePriceFocus}
                onBlur={handlePriceBlur}
                placeholder="$0"
                className={errors.specialPrice ? 'error' : ''}
                disabled={saving}
              />
              {errors.specialPrice && <span className="error-message">{errors.specialPrice}</span>}
              <small className="form-hint">
                Si tiene valor, se mostrará este precio en lugar del precio base. Dejar en 0 para usar precio base.
              </small>
            </div>
          </div>
          <p className="form-hint">
            💡 El stock se gestiona desde el módulo de Stock después de crear el producto.
          </p>

          {formData.price && (
            <div className="price-summary">
              <div className="price-summary-row">
                <span>Precio Base:</span>
                <strong>{formatCurrencyInput(formData.price, true)}</strong>
              </div>
              {categoryCosts.length > 0 && (
                <div className="price-summary-row">
                  <span>Costos Heredados:</span>
                  <strong>
                    {formatCurrency(
                      categoryCosts.reduce((sum, cost) => {
                        const inherited = formData.inheritedCosts.find(ic => ic.costId === (cost.id || cost.name))
                        if (inherited && !inherited.excluded) {
                          return sum + (cost.amount || 0)
                        }
                        return sum
                      }, 0)
                    )}
                  </strong>
                </div>
              )}
              {formData.specificCosts.length > 0 && (
                <div className="price-summary-row">
                  <span>Costos Específicos:</span>
                  <strong>
                    {formatCurrency(
                      formData.specificCosts.reduce((sum, cost) => sum + (cost.amount || 0), 0)
                    )}
                  </strong>
                </div>
              )}
              <div className="price-summary-row total">
                <span>Precio Final:</span>
                <strong>{formatCurrencyInput(calculateFinalPrice().toString(), true)}</strong>
              </div>
              {formData.specialPrice && parseFloat(formData.specialPrice) > 0 && (
                <div className="price-summary-row special">
                  <span>Precio Especial/Oferta:</span>
                  <strong>{formatCurrencyInput(formData.specialPrice, true)}</strong>
                </div>
              )}
            </div>
          )}

          <h4 className="subsection-title">Descripción</h4>
          <div className="form-group">
            <label>
              Descripción Corta
            </label>
            <input
              type="text"
              name="shortDescription"
              value={formData.shortDescription}
              onChange={handleChange}
              placeholder="Breve descripción para el catálogo..."
              maxLength={200}
              disabled={saving}
            />
            <small className="form-hint">
              {formData.shortDescription.length}/200 caracteres
            </small>
          </div>

          <div className="form-group">
            <label>
              Descripción Completa <span className="required">*</span>
            </label>
            <RichTextEditor
              value={formData.description}
              onChange={handleDescriptionChange}
              placeholder="Descripción detallada del producto con formato..."
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          <h4 className="subsection-title">Características Físicas</h4>
          <div className="form-grid">
            <ColorPicker
              value={formData.color}
              onChange={handleColorChange}
              label="Color Principal"
            />

            <div className="form-group">
              <label htmlFor="material">Material</label>
              <input
                type="text"
                id="material"
                name="material"
                value={formData.material}
                onChange={handleChange}
                placeholder="Ej: Madera de roble, Tela premium"
                disabled={saving}
              />
            </div>
          </div>

          <h4 className="subsection-title">Dimensiones (en cm)</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="width">Ancho</label>
              <input
                type="number"
                id="width"
                name="dimensions.width"
                value={formData.dimensions.width}
                onChange={handleDimensionChange}
                placeholder="Ej: 200"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="height">Alto</label>
              <input
                type="number"
                id="height"
                name="dimensions.height"
                value={formData.dimensions.height}
                onChange={handleDimensionChange}
                placeholder="Ej: 85"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="depth">Profundidad</label>
              <input
                type="number"
                id="depth"
                name="dimensions.depth"
                value={formData.dimensions.depth}
                onChange={handleDimensionChange}
                placeholder="Ej: 90"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="weight">Peso (kg)</label>
              <input
                type="number"
                id="weight"
                name="dimensions.weight"
                value={formData.dimensions.weight}
                onChange={handleDimensionChange}
                placeholder="Ej: 35"
                disabled={saving}
              />
            </div>
          </div>
        </div>
        ) : null}

        {/* Sección: Costos Asociados */}
        {activeTab === 'costos' && formData.categoryId && (
          <div className="form-section">
            <h3 className="section-title">Costos Asociados</h3>
            <p className="form-hint">
              Los costos de preparación se suman al precio base para formar el precio final de venta.
            </p>
            
            {/* Costos Heredados de la Categoría */}
            {categoryCosts.length > 0 && (
              <div className="costs-section">
                <h4 className="costs-subtitle">Costos Heredados de la Categoría</h4>
                <div className="costs-list">
                  {categoryCosts.map((cost) => {
                    const inherited = formData.inheritedCosts.find(ic => ic.costId === (cost.id || cost.name))
                    const isExcluded = inherited?.excluded || false
                    
                    return (
                      <div key={cost.id || cost.name} className={`cost-item ${isExcluded ? 'excluded' : ''}`}>
                        <div className="cost-item-info">
                          <div className="cost-item-header">
                            <span className="cost-name">{cost.name}</span>
                            <span className="cost-amount">{formatCurrency(cost.amount || 0)}</span>
                          </div>
                          {cost.description && (
                            <p className="cost-description">{cost.description}</p>
                          )}
                        </div>
                        <label className="cost-exclude-checkbox">
                          <input
                            type="checkbox"
                            checked={isExcluded}
                            onChange={() => toggleInheritedCost(cost.id || cost.name)}
                            disabled={saving}
                          />
                          <span>{isExcluded ? 'Excluido' : 'Incluido'}</span>
                        </label>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            
            {/* Costos Específicos del Producto */}
            <div className="costs-section">
              <h4 className="costs-subtitle">Costos Específicos del Producto</h4>
              
              {formData.specificCosts.length > 0 && (
                <div className="costs-list">
                  {formData.specificCosts.map((cost, index) => (
                    <div key={cost.id || index} className="cost-item">
                      <div className="cost-item-info">
                        <div className="cost-item-header">
                          <span className="cost-name">{cost.name}</span>
                          <span className="cost-amount">{formatCurrency(cost.amount || 0)}</span>
                        </div>
                        {cost.description && (
                          <p className="cost-description">{cost.description}</p>
                        )}
                      </div>
                      <div className="cost-item-actions">
                        <button
                          type="button"
                          onClick={() => handleEditSpecificCost(index)}
                          className="action-btn edit-btn"
                          disabled={saving}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteSpecificCost(index)}
                          className="action-btn delete-btn"
                          disabled={saving}
                          title="Eliminar"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Formulario para agregar/editar costo específico */}
              <div className="cost-form">
                <div className="cost-form-row">
                  <div className="form-group">
                    <label htmlFor="specificCostName">Nombre del Costo</label>
                    <input
                      type="text"
                      id="specificCostName"
                      value={newSpecificCost.name}
                      onChange={(e) => setNewSpecificCost(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ej: Instalación especial"
                      disabled={saving}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="specificCostAmount">Monto</label>
                    <input
                      type="text"
                      id="specificCostAmount"
                      value={
                        editingFields.has('specificCostAmount')
                          ? (newSpecificCost.amount ? `$${newSpecificCost.amount.replace('.', ',')}` : '')
                          : (newSpecificCost.amount ? formatCurrencyInput(newSpecificCost.amount, true) : '')
                      }
                      onChange={(e) => {
                        let cleanedValue = e.target.value.replace(/[^0-9.,]/g, '')
                        if (!cleanedValue) {
                          setNewSpecificCost(prev => ({ ...prev, amount: '' }))
                          return
                        }
                        cleanedValue = cleanedValue.replace('.', ',')
                        const parts = cleanedValue.split(',')
                        if (parts.length > 2) {
                          cleanedValue = parts[0] + ',' + parts.slice(1).join('')
                        }
                        setNewSpecificCost(prev => ({ ...prev, amount: cleanedValue }))
                      }}
                      onFocus={() => setEditingFields(prev => new Set(prev).add('specificCostAmount'))}
                      onBlur={() => {
                        const numericValue = parseCurrencyInput(newSpecificCost.amount || '0')
                        setNewSpecificCost(prev => ({ ...prev, amount: numericValue > 0 ? numericValue.toString() : '' }))
                        setEditingFields(prev => {
                          const newSet = new Set(prev)
                          newSet.delete('specificCostAmount')
                          return newSet
                        })
                      }}
                      placeholder="$0"
                      disabled={saving}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="specificCostDescription">Descripción (opcional)</label>
                  <input
                    type="text"
                    id="specificCostDescription"
                    value={newSpecificCost.description}
                    onChange={(e) => setNewSpecificCost(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descripción del costo..."
                    disabled={saving}
                  />
                </div>
                <div className="cost-form-actions">
                  {editingSpecificCostIndex !== null ? (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancelSpecificCostEdit}
                        disabled={saving}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={handleSaveSpecificCost}
                        disabled={saving}
                      >
                        Guardar Cambios
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleAddSpecificCost}
                      disabled={saving}
                    >
                      + Agregar Costo Específico
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sección: Variantes */}
        {activeTab === 'variantes' ? (
        <div className="form-section">
          <h3 className="section-title">Variantes del Producto</h3>
          <p className="section-description">
            Agrega variantes como diferentes tamaños, colores o materiales
          </p>

          <div className="variants-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="variantType">Tipo de Variante</label>
                <select
                  id="variantType"
                  value={variantType}
                  onChange={(e) => setVariantType(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Seleccionar tipo</option>
                  <option value="size">Tamaño</option>
                  <option value="color">Color</option>
                  <option value="material">Material</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="variantName">Nombre</label>
                <input
                  type="text"
                  id="variantName"
                  value={variantName}
                  onChange={(e) => setVariantName(e.target.value)}
                  placeholder="Ej: Grande, Rojo, Cuero"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="variantPrice">Precio Adicional</label>
                <input
                  type="text"
                  id="variantPrice"
                  value={
                    editingFields.has('variantPrice')
                      ? (variantPrice ? `$${variantPrice.replace('.', ',')}` : '')
                      : (variantPrice ? formatCurrencyInput(variantPrice, true) : '')
                  }
                  onChange={(e) => {
                    let cleanedValue = e.target.value.replace(/[^0-9.,]/g, '')
                    if (!cleanedValue) {
                      setVariantPrice('')
                      return
                    }
                    cleanedValue = cleanedValue.replace('.', ',')
                    const parts = cleanedValue.split(',')
                    if (parts.length > 2) {
                      cleanedValue = parts[0] + ',' + parts.slice(1).join('')
                    }
                    setVariantPrice(cleanedValue)
                  }}
                  onFocus={() => setEditingFields(prev => new Set(prev).add('variantPrice'))}
                  onBlur={() => {
                    const numericValue = parseCurrencyInput(variantPrice || '0')
                    setVariantPrice(numericValue > 0 ? numericValue.toString() : '')
                    setEditingFields(prev => {
                      const newSet = new Set(prev)
                      newSet.delete('variantPrice')
                      return newSet
                    })
                  }}
                  placeholder="$0"
                  disabled={saving}
                />
                <small className="form-hint">Dejar vacío para usar precio base</small>
              </div>

              <div className="form-group">
                <label htmlFor="variantSku">SKU de Variante</label>
                <div className="inline-field">
                  <input
                    type="text"
                    id="variantSku"
                    value={variantSku}
                    onChange={(e) => setVariantSku(e.target.value)}
                    placeholder="Ej: SOF-OSL-001-ROJO"
                    disabled={saving}
                  />
                  {rules?.productIdentification?.skuAuto?.enabled === true ? (
                    <Button
                      type="button"
                      size="small"
                      variant="outline"
                      className="inline-icon-btn"
                      onClick={async () => {
                        try {
                          const skuPayload = {
                            name: formData.name,
                            brand: formData.brand,
                            collection: formData.collection,
                            color: variantType === 'color' ? variantName : formData.color,
                            colorName: variantType === 'color' ? variantName : formData.colorName,
                            sizes: variantType === 'size' ? [variantName] : []
                          }
                          const { sku } = await generateSkuForProduct({ ...skuPayload, id }, rules)
                          setVariantSku(sku.toUpperCase())
                          setNotification({ type: 'success', message: 'SKU de variante generado' })
                          setTimeout(() => setNotification(null), 2500)
                        } catch (e) {
                          setNotification({ type: 'error', message: e?.message || 'No se pudo generar SKU' })
                          setTimeout(() => setNotification(null), 5000)
                        }
                      }}
                      disabled={saving || !variantType || !variantName}
                      title="Generar SKU"
                    >
                      ↻
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="variantSerialNumber">Número de Serie</label>
                <div className="inline-field">
                  <input
                    type="text"
                    id="variantSerialNumber"
                    value={variantSerialNumber}
                    onChange={(e) => setVariantSerialNumber(e.target.value)}
                    placeholder="Ej: 000000000000001"
                    disabled={saving}
                  />
                  {rules?.productIdentification?.serialAuto?.enabled === true ? (
                    <Button
                      type="button"
                      size="small"
                      variant="outline"
                      className="inline-icon-btn"
                      onClick={async () => {
                        try {
                          const digits = rules?.productIdentification?.serialAuto?.digits ?? 15
                          const serial = await generateSerialNumber(digits)
                          setVariantSerialNumber(serial)
                          setNotification({ type: 'success', message: 'Número de serie generado' })
                          setTimeout(() => setNotification(null), 2500)
                        } catch (e) {
                          setNotification({ type: 'error', message: e?.message || 'No se pudo generar número de serie' })
                          setTimeout(() => setNotification(null), 5000)
                        }
                      }}
                      disabled={saving}
                      title="Generar número de serie"
                    >
                      ↻
                    </Button>
                  ) : null}
                </div>
              </div>

            </div>

            <div className="form-group">
              <label>Imágenes de la Variante</label>
              <div className="image-upload-area">
                <input
                  type="file"
                  id="variantImages"
                  accept="image/*"
                  multiple
                  onChange={handleVariantImageChange}
                  disabled={saving || uploadingVariantImages}
                  className="file-input"
                />
                <label htmlFor="variantImages" className="file-label">
                  <span className="upload-icon">📷</span>
                  <span>
                    {uploadingVariantImages ? 'Subiendo imágenes...' : 'Haz clic para seleccionar imágenes'}
                  </span>
                  <small>Máximo 10 imágenes. Se subirán automáticamente a Cloudinary</small>
                </label>
              </div>

              {uploadingVariantImages && Object.keys(variantUploadProgress).length > 0 && (
                <div className="upload-progress">
                  {Object.entries(variantUploadProgress).map(([fileName, progress]) => (
                    <div key={fileName} className="progress-item">
                      <span>{fileName}</span>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span>{progress}%</span>
                    </div>
                  ))}
                </div>
              )}

              {variantImagePreviews.length > 0 && (
                <div className="image-previews">
                  {variantImagePreviews.map((preview, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={preview} alt={`Preview ${index + 1}`} />
                      <button
                        type="button"
                        onClick={() => removeVariantImage(index)}
                        className="remove-image-btn"
                        disabled={saving || uploadingVariantImages}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="variantVideoInput">Videos Embebidos</label>
              <div className="inline-field">
                <input
                  type="url"
                  id="variantVideoInput"
                  value={variantVideoInput}
                  onChange={(e) => setVariantVideoInput(e.target.value)}
                  placeholder="https://www.youtube.com/embed/..."
                  disabled={saving}
                />
                <Button
                  type="button"
                  size="small"
                  variant="outline"
                  className="inline-icon-btn"
                  onClick={handleAddVariantVideo}
                  disabled={saving || !variantVideoInput.trim()}
                  title="Agregar video"
                >
                  +
                </Button>
              </div>
              {variantVideos.length > 0 && (
                <div className="variant-video-list">
                  {variantVideos.map((video, index) => (
                    <div key={`${video}-${index}`} className="variant-video-item">
                      <span>{video}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveVariantVideo(index)}
                        className="remove-variant-btn"
                        disabled={saving}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleAddVariant}
              disabled={saving || uploadingVariantImages || !variantType || !variantName}
            >
              ➕ Agregar Variante
            </Button>
          </div>

          {/* Lista de variantes */}
          {formData.variants.length > 0 && (
            <div className="variants-list">
              <h4>Variantes Agregadas</h4>
              <div className="variants-table">
                <table>
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Nombre</th>
                      <th>Precio</th>
                      <th>SKU</th>
                      <th>Serie</th>
                      <th>Imágenes</th>
                      <th>Videos</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.variants.map((variant, index) => (
                      <tr key={index}>
                        <td>{variant.type}</td>
                        <td>{variant.name}</td>
                        <td>
                          {variant.price 
                            ? `$${variant.price.toLocaleString()}` 
                            : 'Precio base'}
                        </td>
                        <td><code>{variant.sku || '-'}</code></td>
                        <td><code>{variant.serialNumber || '-'}</code></td>
                        <td>{Array.isArray(variant.images) ? variant.images.length : 0}</td>
                        <td>{Array.isArray(variant.videos) ? variant.videos.length : 0}</td>
                        <td>
                          <div className="variant-actions">
                            {isEditing && (
                              <button
                                type="button"
                                onClick={() => openStockModal(variant, index)}
                                className="add-stock-variant-btn"
                                title="Agregar stock"
                                disabled={saving}
                              >
                                📥
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => beginEditVariant(index)}
                              className="edit-variant-btn"
                              disabled={saving}
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveVariant(index)}
                              className="remove-variant-btn"
                              disabled={saving}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {editingVariantIndex !== null ? (
            <div className="variant-edit-panel">
              <h4>Editar variante</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="editVariantType">Tipo de Variante</label>
                  <select
                    id="editVariantType"
                    value={editVariantType}
                    onChange={(e) => setEditVariantType(e.target.value)}
                    disabled={saving}
                  >
                    <option value="">Seleccionar tipo</option>
                    <option value="size">Tamaño</option>
                    <option value="color">Color</option>
                    <option value="material">Material</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="editVariantName">Nombre</label>
                  <input
                    type="text"
                    id="editVariantName"
                    value={editVariantName}
                    onChange={(e) => setEditVariantName(e.target.value)}
                    placeholder="Ej: Grande, Rojo, Cuero"
                    disabled={saving}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editVariantPrice">Precio Adicional</label>
                  <input
                    type="number"
                    id="editVariantPrice"
                    value={editVariantPrice}
                    onChange={(e) => setEditVariantPrice(e.target.value)}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    disabled={saving}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editVariantSku">SKU de Variante</label>
                  <div className="inline-field">
                    <input
                      type="text"
                      id="editVariantSku"
                      value={editVariantSku}
                      onChange={(e) => setEditVariantSku(e.target.value)}
                      placeholder="Ej: SOF-OSL-001-ROJO"
                      disabled={saving}
                    />
                    {rules?.productIdentification?.skuAuto?.enabled === true ? (
                      <Button
                        type="button"
                        size="small"
                        variant="outline"
                        className="inline-icon-btn"
                        onClick={async () => {
                          try {
                            const skuPayload = {
                              name: formData.name,
                              brand: formData.brand,
                              collection: formData.collection,
                              color: editVariantType === 'color' ? editVariantName : formData.color,
                              colorName: editVariantType === 'color' ? editVariantName : formData.colorName,
                              sizes: editVariantType === 'size' ? [editVariantName] : []
                            }
                            const { sku } = await generateSkuForProduct({ ...skuPayload, id }, rules)
                            setEditVariantSku(sku.toUpperCase())
                            setNotification({ type: 'success', message: 'SKU de variante generado' })
                            setTimeout(() => setNotification(null), 2500)
                          } catch (e) {
                            setNotification({ type: 'error', message: e?.message || 'No se pudo generar SKU' })
                            setTimeout(() => setNotification(null), 5000)
                          }
                        }}
                        disabled={saving || !editVariantType || !editVariantName}
                        title="Generar SKU"
                      >
                        ↻
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="editVariantSerialNumber">Número de Serie</label>
                  <div className="inline-field">
                    <input
                      type="text"
                      id="editVariantSerialNumber"
                      value={editVariantSerialNumber}
                      onChange={(e) => setEditVariantSerialNumber(e.target.value)}
                      placeholder="Ej: 000000000000001"
                      disabled={saving}
                    />
                    {rules?.productIdentification?.serialAuto?.enabled === true ? (
                      <Button
                        type="button"
                        size="small"
                        variant="outline"
                        className="inline-icon-btn"
                        onClick={async () => {
                          try {
                            const digits = rules?.productIdentification?.serialAuto?.digits ?? 15
                            const serial = await generateSerialNumber(digits)
                            setEditVariantSerialNumber(serial)
                            setNotification({ type: 'success', message: 'Número de serie generado' })
                            setTimeout(() => setNotification(null), 2500)
                          } catch (e) {
                            setNotification({ type: 'error', message: e?.message || 'No se pudo generar número de serie' })
                            setTimeout(() => setNotification(null), 5000)
                          }
                        }}
                        disabled={saving}
                        title="Generar número de serie"
                      >
                        ↻
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="editVariantImages">Imágenes de la Variante</label>
                <div className="image-upload-area">
                  <input
                    type="file"
                    id="editVariantImages"
                    accept="image/*"
                    multiple
                    onChange={handleEditVariantImageChange}
                    disabled={saving || uploadingEditVariantImages}
                    className="file-input"
                  />
                  <label htmlFor="editVariantImages" className="file-label">
                    <span className="upload-icon">📷</span>
                    <span>
                      {uploadingEditVariantImages ? 'Subiendo imágenes...' : 'Haz clic para seleccionar imágenes'}
                    </span>
                  </label>
                </div>
                {editVariantImages.length > 0 && (
                  <div className="image-previews">
                    {editVariantImages.map((img, index) => (
                      <div key={img?.url || img?.thumbnailUrl || index} className="image-preview-item">
                        <img src={img.thumbnailUrl || img.url} alt={`Preview ${index + 1}`} />
                        <button
                          type="button"
                          onClick={() => removeEditVariantImage(index)}
                          className="remove-image-btn"
                          disabled={saving || uploadingEditVariantImages}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="editVariantVideoInput">Videos Embebidos</label>
                <div className="inline-field">
                  <input
                    type="url"
                    id="editVariantVideoInput"
                    value={editVariantVideoInput}
                    onChange={(e) => setEditVariantVideoInput(e.target.value)}
                    placeholder="https://www.youtube.com/embed/..."
                    disabled={saving}
                  />
                  <Button
                    type="button"
                    size="small"
                    variant="outline"
                    className="inline-icon-btn"
                    onClick={handleAddEditVariantVideo}
                    disabled={saving || !editVariantVideoInput.trim()}
                    title="Agregar video"
                  >
                    +
                  </Button>
                </div>
                {editVariantVideos.length > 0 && (
                  <div className="variant-video-list">
                    {editVariantVideos.map((video, index) => (
                      <div key={`${video}-${index}`} className="variant-video-item">
                        <span>{video}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveEditVariantVideo(index)}
                          className="remove-variant-btn"
                          disabled={saving}
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="variant-edit-actions">
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelEditVariant}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleSaveEditedVariant}
                  disabled={saving || uploadingEditVariantImages}
                >
                  Guardar Cambios
                </Button>
              </div>
            </div>
          ) : null}
        </div>
        ) : null}

        {/* Sección: Estado */}
        {activeTab === 'estado' ? (
        <div className="form-section">
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
                disabled={saving}
              />
              <span>Producto activo (visible en el catálogo público)</span>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
                disabled={saving}
              />
              <span>⭐ Producto destacado (aparecerá en la página principal)</span>
            </label>
            <small className="form-hint">
              Los productos destacados tienen prioridad en el carrusel de la página de inicio
            </small>
          </div>
        </div>
        ) : null}

        {/* Botones de acción */}
        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/productos')}
            disabled={saving || uploadingVariantImages}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || uploadingVariantImages}
          >
            {saving 
              ? (isEditing ? 'Guardando...' : 'Creando...') 
              : (isEditing ? 'Guardar Cambios' : 'Crear Producto')}
          </Button>
        </div>
      </form>

      {stockModalData && (
        <VariantStockModal
          isOpen={!!stockModalData}
          onClose={() => setStockModalData(null)}
          variant={stockModalData.variant}
          variantIndex={stockModalData.variantIndex}
          productId={stockModalData.productId}
          onStockSaved={() => setStockModalData(null)}
        />
      )}
    </div>
  )
}

export default ProductForm
